import {
  FundingSourceSelector,
  type ExternalAccount,
} from '@/src/common/components/fundingSourceSelector'
import { Button } from '@/src/common/components/Button'
import { useEffect, useState } from 'react'
import { ScrollView } from 'react-native'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { PlaidItemStatus } from '@api/mesa/graphql'
import type { PaymentMethods } from '@/src/experiences/card-payments/providePaymentMethods'
import { useEvent } from 'tamagui'

export function BaseFundingSource({
  storedAccount,
  setStoredAccount,
  pushToNextScreen,
  testID,
  accountListTitle,
  promptForUpdate = false,
  paymentMethods,
  loading,
}: {
  storedAccount?: ExternalAccount
  setStoredAccount?: (account: ExternalAccount) => void
  pushToNextScreen?: () => void
  testID?: string
  accountListTitle?: string
  promptForUpdate?: boolean
  paymentMethods?: PaymentMethods
  loading?: boolean
}) {
  const Alert = useAlert('Funding Source')

  const [selectedAccount, setSelectedAccount] = useState<ExternalAccount>()
  const onDataChange = useEvent(() =>
    setSelectedAccount(storedAccount ?? (promptForUpdate ? undefined : paymentMethods?.data?.[0])),
  )

  useEffect(() => onDataChange(), [onDataChange])

  const onContinuePress = () => {
    if (selectedAccount?.isVerified) {
      setStoredAccount?.(selectedAccount)
      pushToNextScreen?.()
    } else {
      Alert.alert(
        'Funding Source',
        `You can only make payments with verified accounts. Please verify your account and try again`,
        [{ text: 'OK', style: 'default' }],
      )
    }
  }

  const accountAvailable = paymentMethods?.data || []

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 50 }}>
      <FundingSourceSelector
        title={accountListTitle}
        selectedAccount={selectedAccount}
        onAccountSelectedChange={(account) => {
          if (promptForUpdate && account.status === PlaidItemStatus.UpdateRequired) {
            void paymentMethods?.openPlaidLink?.(account)
          } else {
            setSelectedAccount(account)
          }
        }}
        loading={paymentMethods?.loading || paymentMethods?.plaidLinkLoading || loading}
        accounts={accountAvailable}
        onAddNewAccountPress={() => void paymentMethods?.openPlaidLink?.()}
        promptForUpdate={promptForUpdate}
      />
      <Button
        testID={testID}
        title="Continue"
        onPress={onContinuePress}
        disabled={!selectedAccount}
      />
    </ScrollView>
  )
}
